import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import axios from 'axios';
import { appUrl } from '../../../../../../utils/config';
import jwtDecode from 'jwt-decode';

// The one document type that carries a per-shipment identity (a batch can have more than
// one concurrent Delivery ASN / Post-Shipment record, one per delivery). Selecting it opens
// the COI Field / Value columns so the user can pin which delivery this row is resubmitting.
const ASN_DOCUMENT_TYPE = 'Delivery ASN IDoc and Post-Shipment Documents';
// Currently the only digitalized field the backend exposes for the Delivery ASN — kept as
// a list (not a hardcoded string) so adding more fields later only means extending this.
const COI_FIELD_OPTIONS = ['Delivery Number'];

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    padding: theme.spacing(2, 3),
    position: 'relative',
  },
  cancelButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#000',
    fontSize: '1rem',
    fontWeight: 500,
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  titleText: {
    fontWeight: 600,
    fontSize: '1.5rem',
    color: '#000',
    textAlign: 'center',
    width: '100%',
  },
  dialogContent: {
    padding: theme.spacing(2, 3),
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  extractedDataSection: {
    backgroundColor: '#E8F4F8',
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
    borderRadius: '4px',
  },
  dataRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  dataLabel: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#000',
    minWidth: '120px',
  },
  dataValue: {
    fontSize: '0.875rem',
    color: '#000',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    color: '#000',
  },
  documentTable: {
    marginBottom: theme.spacing(2),
    '& .MuiTableCell-root': {
      padding: theme.spacing(1.5, 2),
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      borderBottom: '1px solid #E0E0E0',
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
      borderBottom: '1px solid #E0E0E0',
    },
    '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  tableHeader: {
    fontWeight: 600,
    backgroundColor: '#F5F5F5',
    color: '#000',
  },
  documentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  errorText: {
    color: '#FF0000',
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  addRowButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
    textTransform: 'none',
    color: '#000',
    border: '1px solid #000',
    borderRadius: '20px',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  noteText: {
    fontSize: '0.75rem',
    color: '#666',
    marginBottom: theme.spacing(2),
    fontFamily: theme.typography.fontFamily,
    fontWeight: 400,
  },
  commentsLabel: {
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1),
    color: '#000',
  },
  commentsField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      '& textarea.MuiInputBase-input': {
        lineHeight: '20px',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: theme.spacing(0),
    },
  },
  commentsHelperText: {
    textAlign: 'right',
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
    color: '#666',
  },
  sendButton: {
    backgroundColor: '#e36e39',
    color: '#FFFFFF',
    borderRadius: '20px',
    padding: '8px 32px',
    textTransform: 'none',
    fontWeight: 500,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: '#c44d15',
      color: 'white',
      boxShadow: '0 4px 12px rgba(227, 110, 57, 0.4)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 6px rgba(227, 110, 57, 0.3)',
    },
    '&:disabled': {
      backgroundColor: '#CCCCCC',
      color: '#666666',
      cursor: 'not-allowed',
    },
  },
  dialogActions: {
    padding: theme.spacing(1, 3, 2, 3),
    justifyContent: 'center',
    paddingBottom: theme.spacing(3),
  },
  selectField: {
    '& .MuiOutlinedInput-root': {
      height: '40px',
    },
  },
}));

const emptyRow = () => ({ documentType: '', coiField: '', value: '' });

const DocumentResubmissionDialog = ({ open, onClose, batchData, reasonForReset, onSuccess }) => {
  const classes = useStyles();
  // One row per document type being resubmitted. The ASN document type is the only one
  // allowed to appear in more than one row (one per delivery number).
  const [rows, setRows] = useState([emptyRow()]);
  const [comments, setComments] = useState('');
  const [commentsError, setCommentsError] = useState('');
  const [documentError, setDocumentError] = useState('');
  const [loading, setLoading] = useState(false);
  const [availableDocumentTypes, setAvailableDocumentTypes] = useState([]);
  const [deliveryNumbers, setDeliveryNumbers] = useState([]);
  const [fetchingDocuments, setFetchingDocuments] = useState(false);

  // Fetch document types (and, for the ASN type, the batch's delivery numbers) from API
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!open || !batchData?.vendorMosymphonyID || !batchData?.schrockenDeliveryScheduleBatchID) {
        return;
      }

      setFetchingDocuments(true);
      try {
        const payload = {
          vendorMosymphonyID: batchData.vendorMosymphonyID,
          schrockenDeliveryScheduleBatchID: batchData.schrockenDeliveryScheduleBatchID,
        };

        const response = await axios.post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDocuments`,
          payload
        );

        if (response.data?.status && Array.isArray(response.data.data)) {
          setAvailableDocumentTypes(response.data.data);
          // Additive — older API responses without this field simply yield [], leaving the
          // ASN row's Value dropdown empty rather than breaking anything else.
          setDeliveryNumbers(Array.isArray(response.data.deliveryNumbers) ? response.data.deliveryNumbers : []);
        } else {
          setAvailableDocumentTypes([]);
          setDeliveryNumbers([]);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
        setAvailableDocumentTypes([]);
        setDeliveryNumbers([]);
      } finally {
        setFetchingDocuments(false);
      }
    };

    fetchDocuments();
  }, [open, batchData]);

  // Reset selected rows when dialog opens
  useEffect(() => {
    if (open) {
      setRows([emptyRow()]);
      setComments('');
      setCommentsError('');
      setDocumentError('');
    }
  }, [open]);

  // Delivery numbers already picked by other ASN rows, excluded from this row's Value
  // dropdown so the same delivery number can't be selected twice.
  const usedDeliveryNumbersForRow = (rowIndex) =>
    rows
      .filter((row, i) => i !== rowIndex && row.documentType === ASN_DOCUMENT_TYPE && row.value)
      .map((row) => row.value);

  // A non-ASN document type already used by another row is disabled in this row's dropdown
  // (can't resubmit the same document type twice). The ASN type is allowed in multiple rows —
  // one per unique delivery number — but once every delivery number is already claimed by
  // OTHER rows, it's disabled too for any row that doesn't already have it selected, since
  // there'd be nothing left to assign (this row keeps it selectable for itself, so its own
  // current selection never gets disabled out from under it).
  const isDocumentTypeDisabledForRow = (type, rowIndex) => {
    if (type !== ASN_DOCUMENT_TYPE) {
      return rows.some((row, i) => i !== rowIndex && row.documentType === type);
    }
    if (rows[rowIndex]?.documentType === ASN_DOCUMENT_TYPE) return false;
    if (deliveryNumbers.length === 0) return false;
    return usedDeliveryNumbersForRow(rowIndex).length >= deliveryNumbers.length;
  };

  // True once every document type — including every delivery-number slot of the ASN type —
  // is already claimed by an existing row, i.e. a new row would have nothing selectable in
  // its Document Type dropdown. rows.length as the hypothetical index for "a row that
  // doesn't exist yet" reuses the same per-row disabling logic a real new row would see.
  const noDocumentTypesLeftForNewRow =
    availableDocumentTypes.length > 0 &&
    availableDocumentTypes.every((type) => isDocumentTypeDisabledForRow(type, rows.length));

  const handleAddRow = () => {
    setRows([...rows, emptyRow()]);
  };

  const handleRemoveRow = (index) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleRowDocumentTypeChange = (index, newType) => {
    setRows(rows.map((row, i) => {
      if (i !== index) return row;
      // Auto-select the only current COI Field option when switching to the ASN type;
      // clear COI Field/Value otherwise so a stale selection can't linger for a plain
      // document type that doesn't use those columns.
      return {
        documentType: newType,
        coiField: newType === ASN_DOCUMENT_TYPE ? COI_FIELD_OPTIONS[0] : '',
        value: '',
      };
    }));
    if (documentError) {
      setDocumentError('');
    }
  };

  const handleRowCoiFieldChange = (index, newField) => {
    setRows(rows.map((row, i) => (i === index ? { ...row, coiField: newField, value: '' } : row)));
  };

  const handleRowValueChange = (index, newValue) => {
    setRows(rows.map((row, i) => (i === index ? { ...row, value: newValue } : row)));
    if (documentError) {
      setDocumentError('');
    }
  };

  const handleCommentsChange = (e) => {
    const value = e.target.value;
    if (value.length <= 255) {
      setComments(value);
      setCommentsError('');
    } else {
      setCommentsError('Comments cannot exceed 255 characters');
    }
  };

  const handleSendRequest = async () => {
    // Clear previous errors
    setCommentsError('');
    setDocumentError('');

    if (comments.length > 255) {
      setCommentsError('Comments cannot exceed 255 characters');
      return;
    }

    // Every row on screen must have a document type — a blank row means the user hasn't
    // finished it yet (or it should be removed), not that it's silently skipped.
    const emptyRowExists = rows.some((row) => !row.documentType);
    if (emptyRowExists) {
      setDocumentError('Please select a document type for every row, or remove the empty row');
      return;
    }
    const incompleteAsnRow = rows.some(
      (row) => row.documentType === ASN_DOCUMENT_TYPE && (!row.coiField || !row.value)
    );
    if (incompleteAsnRow) {
      setDocumentError(`Please select a ${COI_FIELD_OPTIONS[0]} for each ${ASN_DOCUMENT_TYPE} row`);
      return;
    }

    setLoading(true);
    try {
      // Prepare document types as array of objects with documentTypeName and resubmitted
      // (always false). ASN rows additionally carry which delivery this row targets.
      // Per the /updateFGBatchForWorkflowReset contract, each ASN row's chosen delivery
      // number goes out as a flat `deliveryNumber` field — coiField/value are UI-only
      // concepts (today the only COI Field is "Delivery Number") and aren't sent as-is.
      const documentTypesForResubmission = rows.map((row) => ({
        documentTypeName: row.documentType,
        resubmitted: false,
        ...(row.documentType === ASN_DOCUMENT_TYPE && {
          deliveryNumber: row.value,
        }),
      }));

      // Determine documentResubmissionRequested and resubmitDocumentDigitizationRequired
      // based on reasonForReset
      // documentResubmissionRequested = true for BOTH document resubmission options
      // resubmitDocumentDigitizationRequired = true only for "Document Resubmission with data extracted from digitalized document"
      const documentResubmissionRequested = true; // Always true for document resubmission options
      
      const resubmitDocumentDigitizationRequired = 
        reasonForReset === 'Document Resubmission with data extracted from digitalized document';

      // Get collaboratorTypeID from JWT token
      let collaboratorTypeID = 1; // Default fallback
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          const tokenData = jwtDecode(token);
          collaboratorTypeID = tokenData.collaborator_type_id || 1;
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }

      const requestData = [{
        schrockenDeliveryScheduleBatchID: batchData?.schrockenDeliveryScheduleBatchID || '',
        poNumber: batchData?.poNumber || '',
        batchNumber: batchData?.batchNumber || '',
        documentTypesForResubmission: documentTypesForResubmission,
        comments: comments,
        documentResubmissionRequested: documentResubmissionRequested,
        resubmitDocumentDigitizationRequired: resubmitDocumentDigitizationRequired,
        vendorMosymphonyID: batchData?.vendorMosymphonyID || '',
        collaboratorTypeID: collaboratorTypeID,
        // Note: brandOwnerMosymphonyID, lastUpdatedBy, senderLegalName, receiverLegalName, and roleNames
        // will be added by the middleware/backend
      }];

      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateFGBatchForWorkflowReset`,
        requestData
      );

      if (response.data?.status) {
        // Close the main dialog and trigger success dialog in parent
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setCommentsError(response.data?.message || 'Failed to send request. Please try again.');
      }
    } catch (error) {
      console.error('Error sending request to CMO:', error);
      setCommentsError(error.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Extract data from batchData (passed from ResetBatchDialog, which gets it from fetchAllFGBatches)
  const extractedData = batchData || {
    poNumber: 'NA',
    batchNumber: 'NA',
    cmo: 'NA',
    batchStatus: 'NA',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        style: {
          borderRadius: '8px',
          // Widened from 750px so the four-column Document Type / COI Field / Value / Action
          // table fits without forcing a horizontal scrollbar inside the dialog.
          maxWidth: '980px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography
          className={classes.cancelButton}
          onClick={onClose}
        >
          Cancel <CloseIcon style={{ fontSize: '24px', marginLeft: '4px', color: '#FF0000' }} />
        </Typography>
        <Typography className={classes.titleText}>
          {reasonForReset === 'Document Resubmission with data extracted from digitalized document' 
            ? (
              <>
                Document Resubmission with data<br />
                extracted from digitalized document
              </>
            )
            : reasonForReset || 'Document Resubmission'
          }
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Extracted Data Section */}
        <div className={classes.extractedDataSection}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.dataLabel}>PO Number</Typography>
              <Typography className={classes.dataValue}>{extractedData.poNumber}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.dataLabel}>Batch Number</Typography>
              <Typography className={classes.dataValue}>{extractedData.batchNumber}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.dataLabel}>CMO</Typography>
              <Typography className={classes.dataValue}>{extractedData.cmo}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.dataLabel}>Batch Status</Typography>
              <Typography className={classes.dataValue}>{extractedData.batchStatus}</Typography>
            </Grid>
          </Grid>
        </div>

        {/* Document Type / COI Field / Value table — one row per document being resubmitted.
            Selecting the ASN document type on a row opens its COI Field and Value dropdowns;
            every other document type leaves those two cells blank/disabled. */}
        <TableContainer className={classes.documentTable}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>Document Type</TableCell>
                <TableCell className={classes.tableHeader}>COI Field</TableCell>
                <TableCell className={classes.tableHeader}>Value</TableCell>
                <TableCell className={classes.tableHeader}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const isAsnRow = row.documentType === ASN_DOCUMENT_TYPE;
                const valueOptions = deliveryNumbers.filter(
                  (num) => num === row.value || !usedDeliveryNumbersForRow(index).includes(num)
                );
                return (
                  <TableRow key={index}>
                    <TableCell>
                      <FormControl fullWidth variant="outlined" className={classes.selectField}>
                        <Select
                          value={row.documentType}
                          onChange={(e) => handleRowDocumentTypeChange(index, e.target.value)}
                          displayEmpty
                          disabled={fetchingDocuments || availableDocumentTypes.length === 0}
                        >
                          <MenuItem value="" disabled>
                            {fetchingDocuments
                              ? 'Loading...'
                              : availableDocumentTypes.length === 0
                              ? 'No documents to reset for this batch'
                              : 'Select document type'}
                          </MenuItem>
                          {availableDocumentTypes.map((type, typeIndex) => (
                            <MenuItem
                              key={typeIndex}
                              value={type}
                              disabled={isDocumentTypeDisabledForRow(type, index)}
                            >
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      {/* COI Field only ever applies to the ASN document type — every other
                          type shows a plain dash instead of a disabled-looking dropdown. */}
                      {isAsnRow ? (
                        <FormControl fullWidth variant="outlined" className={classes.selectField}>
                          <Select
                            value={row.coiField}
                            onChange={(e) => handleRowCoiFieldChange(index, e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="" disabled>
                              Select field
                            </MenuItem>
                            {COI_FIELD_OPTIONS.map((field) => (
                              <MenuItem key={field} value={field}>
                                {field}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography className={classes.dataValue}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isAsnRow ? (
                        <FormControl fullWidth variant="outlined" className={classes.selectField}>
                          <Select
                            value={row.value}
                            onChange={(e) => handleRowValueChange(index, e.target.value)}
                            displayEmpty
                            disabled={!row.coiField}
                          >
                            <MenuItem value="" disabled>
                              {deliveryNumbers.length === 0 ? 'No delivery numbers' : 'Select value'}
                            </MenuItem>
                            {valueOptions.map((num) => (
                              <MenuItem key={num} value={num}>
                                {num}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography className={classes.dataValue}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Only shown once there's more than one row — a single row has
                          nothing to delete down to. */}
                      {rows.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveRow(index)}
                          aria-label="Remove row"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Button
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddRow}
          className={classes.addRowButton}
          disabled={fetchingDocuments || noDocumentTypesLeftForNewRow}
        >
          Add New Row
        </Button>

        {/* Document Error */}
        {documentError && (
          <Typography className={classes.errorText} style={{ marginTop: '8px', marginBottom: '8px' }}>
            {documentError}
          </Typography>
        )}

        {/* Note */}
        <Typography className={classes.noteText} style={{ marginTop: '16px' }}>
          Note - Re-submission of Post Manufacturing and Pre-Shipment documents is not allowed after IBD/GRN is generated.
        </Typography>

        {/* Comments Section */}
        <Typography className={classes.commentsLabel}>Comments</Typography>
        <div style={{ position: 'relative', marginBottom: commentsError ? '20px' : '0' }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            placeholder="Enter text here"
            value={comments}
            onChange={handleCommentsChange}
            className={classes.commentsField}
            error={!!commentsError}
            helperText={commentsError}
            FormHelperTextProps={{
              style: { margin: 0, marginTop: '4px' }
            }}
          />
          {!commentsError && (
            <Typography 
              style={{ 
                position: 'absolute', 
                right: '14px', 
                bottom: '6px', 
                fontSize: '0.75rem', 
                color: '#666',
                pointerEvents: 'none',
                backgroundColor: 'white',
                padding: '0 2px'
              }}
            >
              {comments.length}/255
            </Typography>
          )}
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleSendRequest}
          className={classes.sendButton}
          disabled={
            !!commentsError ||
            !!documentError ||
            comments.length > 255 ||
            loading ||
            // Every row must have a document type — one blank row among filled ones is
            // just as much a block as all of them being blank.
            rows.some((row) => !row.documentType) ||
            rows.some((row) => row.documentType === ASN_DOCUMENT_TYPE && (!row.coiField || !row.value))
          }
        >
          {loading ? 'Sending...' : 'Send Request to CMO'}
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export default DocumentResubmissionDialog;
